import React, { useState, useEffect } from 'react';
import { Route, BrowserRouter as Router, Routes, Link, Navigate, useLocation } from 'react-router-dom';
import App from '../App';
import UserProfilePage from '../components/User/userProfilePage';
import FormsPage from '../components/Forms/formsPage';
import SettingsPage from '../components/Settings/settingsPage';
import PaymentHistoryPage from './Payments/payments';
import SubjectsListPage from '../components/Subjects/list';
import AddSubjectPage from '../components/Subjects/addSubject';
import ViewSubjectPage from '../components/Subjects/viewSubject';
import EditSubjectPage from '../components/Subjects/editSubject';
import SubjectsHistoryPage from '../components/Subjects/history';
import SubjectsListForAdmin from '../components/Subjects/listForAdmin';
import SubjectsHistoryListPage from '../components/Subjects/historyList';
import ResultsClearPage from '../components/Results/clear';
import ResultsAlertsPage from '../components/Results/alerts';
import ResultsAwaitingPage from '../components/Results/awaiting';
import NotificationsPage from '../components/Notifications/notificationsPage';
import EditEmployeePage from '../components/Admin/Employees/editEmployee';
import ErrorPage from '../components/Error/errorPage';
import Logout from '../components/User/logout';
import Login from '../components/Auth/Login';
import SignUp from '../components/Auth/SignUp';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Logo from '../images/PersonnelgraphLogoLight.svg';
import Badge from 'react-bootstrap/Badge';
import ApiHelper from '../api/apiCallsHelper.js';
import PubSub from 'pubsub-js';
import AddEmployeePage from '../components/Admin/Employees/addEmployee';
import EmployeesListPage from '../components/Admin/Employees/listEmployees';
import ToastPage from '../components/Toasts/toastPage';
import config from '../common/config';
import ProcessingOrdersPage from '../components/Order/ProcessingOrdersList';
import UsersListPage from '../components/Admin/Users/listUsers';
import EditUserPage from '../components/Admin/Users/editUser';
import UserPaymentPage from './Admin/Users/userPayments.jsx';
import FcraPage from '../components/FCRA/fcraPage';
import PublicFcraAuth from '../components/FCRA/PublicFcraAuth';

const ProtectedRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
  return isLoggedIn ? children : <Navigate to="/login" replace />;
};

const isAuthenticated = () => {
  return !!(localStorage.getItem('jwt') || sessionStorage.getItem('jwt'));
};

const getUserRole = () => {
  return localStorage.getItem('userRole') || sessionStorage.getItem('userRole') || '';
};

const getUserName = () => {
  return localStorage.getItem('userName') || sessionStorage.getItem('userName') || '';
};

const Navigation = () => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [role, setRole] = useState('');
  const [isAuth, setIsAuth] = useState(isAuthenticated());
  const location = useLocation();

  // Check auth status on mount, location changes, and auth events
  useEffect(() => {
    const updateAuth = () => {
      const authenticated = isAuthenticated();
      const currentRole = getUserRole();

      setIsAuth(authenticated);
      setRole(currentRole);

      if (authenticated) {
        // Fetch notifications
        const fetchNotifications = async () => {
          try {
            const count = await ApiHelper.getWithJWT(
              `${config.trueFingerPrintsAPI.baseURL}/notifications/seen`
            );
            setNotificationCount(count.count);
          } catch (error) {
            console.error('Failed to fetch notifications:', error);
          }
        };

        fetchNotifications();
      } else {
        // Reset state when not authenticated
        setNotificationCount(0);
      }
    };

    // Initial check
    updateAuth();

    // Listen for custom auth change events
    window.addEventListener('authChange', updateAuth);

    // Subscribe to notification changes
    const token = PubSub.subscribe('change_notification', () => {
      setNotificationCount(prev => (prev === 0 ? 0 : prev - 1));
    });

    return () => {
      window.removeEventListener('authChange', updateAuth);
      PubSub.unsubscribe(token);
    };
  }, [location]); // Re-run when location changes

  // Don't render navbar if not authenticated or on public FCRA pages
  const publicFcraPages = ['/fcra-auth/', '/view-authorization/'];
  const isPublicFcraPage = publicFcraPages.some(page => location.pathname.startsWith(page));

  if (!isAuth || isPublicFcraPage) {
    return null;
  }

  const employeesLink = role === 'sysadmin' ? (
    <Link to='/employees' className='nav-link'>
      Employees
    </Link>
  ) : null;

  const usersLink = role === 'sysadmin' ? (
    <Link to='/users' className='nav-link'>
      Users
    </Link>
  ) : null;

  return (
    <Navbar sticky='top' expand='lg' variant='dark' className='main-navigation'>
      <Navbar.Brand>
        <Link to='/' className='navbar-brand'>
          <img src={Logo} width='250' alt='Personnelgraph-Logo' />
        </Link>
      </Navbar.Brand>
      <Navbar.Toggle aria-controls='responsive-navbar-nav' />
      <Navbar.Collapse id='responsive-navbar-nav'>
        <Nav className='mr-auto'>
          {role === 'user' ? (
            <Link to='/' className='nav-link'>
              Overview
            </Link>
          ) : (
            <span className='navbar-text'>{role}</span>
          )}
          <Link to='/individuals' className='nav-link'>
            Individual Profile
          </Link>
          {/* <Link to='/forms' className='nav-link'>
            Forms
          </Link> */}
          <Link to='/authorization' className='nav-link'>
            Authorization
          </Link>
          {employeesLink}
          {usersLink}
        </Nav>
        <Nav className='ml-auto'>
          <Link to='/notifications' className='nav-link'>
            <i className='material-icons'>notifications</i>
            <Badge pill variant='primary'>
              {notificationCount}
            </Badge>
          </Link>
          <NavDropdown
            title={getUserName() || 'User'}
            id='basic-nav-dropdown'
          >
            <Link to='/processing-orders' className='dropdown-item'>
              <i className='material-icons'>view_list</i>Orders in Process
            </Link>
            <NavDropdown.Divider />
            <Link to='/payments' className='dropdown-item'>
              <i className='material-icons'>monetization_on</i>Payments
            </Link>
            <Link to='/settings' className='dropdown-item'>
              <i className='material-icons'>settings</i>Settings
            </Link>
            <NavDropdown.Divider />
            <Link to='/log-out' className='dropdown-item'>
              <i className='material-icons'>exit_to_app</i>Log Out
            </Link>
          </NavDropdown>
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
};

const AppRoutes = () => {
  const [userRole, setUserRole] = useState(getUserRole());
  const location = useLocation();

  // Update user role when location changes or auth changes
  useEffect(() => {
    const updateRole = () => {
      const role = getUserRole();
      console.log('AppRoutes: authChange event - updating role to:', role);
      setUserRole(role);
    };

    const initialRole = getUserRole();
    console.log('AppRoutes: Initial role on location change:', initialRole, 'path:', location.pathname);
    setUserRole(initialRole);

    window.addEventListener('authChange', updateRole);

    return () => {
      window.removeEventListener('authChange', updateRole);
    };
  }, [location]);

  return (
    <Routes>
      {/* Public Routes - No authentication required */}
      <Route path='/login' element={<Login />} />
      <Route path='/signup' element={<SignUp />} />
      <Route path='/fcra-auth/:unique' element={<PublicFcraAuth />} />
      <Route path='/view-authorization/:unique' element={<PublicFcraAuth />} />
      <Route path='/log-out' element={<Logout />} />

      {/* Protected Routes - All require authentication */}
      <Route
        path='/'
        element={
          <ProtectedRoute>
            {userRole === 'user' ? <App /> : <SubjectsListForAdmin />}
          </ProtectedRoute>
        }
      />
      <Route path='/user-profile' element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
      <Route path='/forms' element={<ProtectedRoute><FormsPage /></ProtectedRoute>} />
      <Route path='/authorization' element={<ProtectedRoute><FcraPage /></ProtectedRoute>} />
      <Route path='/settings' element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path='/payments' element={<ProtectedRoute><PaymentHistoryPage /></ProtectedRoute>} />

      <Route
        path='/individuals'
        element={
          <ProtectedRoute>
            {userRole === 'sysadmin' || userRole === 'employee' ? (
              <SubjectsListForAdmin />
            ) : (
              <SubjectsListPage />
            )}
          </ProtectedRoute>
        }
      />

      <Route path='/add-subject' element={<ProtectedRoute><AddSubjectPage /></ProtectedRoute>} />
      <Route path='/view-subject' element={<ProtectedRoute><ViewSubjectPage /></ProtectedRoute>} />
      <Route path='/edit-subject' element={<ProtectedRoute><EditSubjectPage /></ProtectedRoute>} />
      <Route path='/history' element={<ProtectedRoute><SubjectsHistoryPage /></ProtectedRoute>} />
      <Route path='/history-list' element={<ProtectedRoute><SubjectsHistoryListPage /></ProtectedRoute>} />
      <Route path='/results/clear' element={<ProtectedRoute><ResultsClearPage /></ProtectedRoute>} />
      <Route path='/results/alerts' element={<ProtectedRoute><ResultsAlertsPage /></ProtectedRoute>} />
      <Route path='/results/awaiting' element={<ProtectedRoute><ResultsAwaitingPage /></ProtectedRoute>} />
      <Route path='/notifications' element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path='/employees' element={<ProtectedRoute><EmployeesListPage /></ProtectedRoute>} />
      <Route path='/add-employee' element={<ProtectedRoute><AddEmployeePage /></ProtectedRoute>} />
      <Route path='/editEmployee' element={<ProtectedRoute><EditEmployeePage /></ProtectedRoute>} />
      <Route path='/processing-orders' element={<ProtectedRoute><ProcessingOrdersPage /></ProtectedRoute>} />
      <Route path='/users' element={<ProtectedRoute><UsersListPage /></ProtectedRoute>} />
      <Route path='/editUser' element={<ProtectedRoute><EditUserPage /></ProtectedRoute>} />
      <Route path='/paymentsUser' element={<ProtectedRoute><UserPaymentPage /></ProtectedRoute>} />

      {/* 404 - Redirect to login if not authenticated, otherwise show error */}
      <Route path='*' element={<ProtectedRoute><ErrorPage /></ProtectedRoute>} />
    </Routes>
  );
};

const NavRouter = () => {
  return (
    <Router>
      <Navigation />
      <AppRoutes />
      <ToastPage />
    </Router>
  );
};

export default NavRouter;
